import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import useFiltersVariables from "components/Filters/useFiltersVariables";

import { getStackPredicate } from "../helpers";
import { FILTERS_TYPE, initialSortDirection, initialSortOption } from "../ListLayout/constants";
import {
  SEARCH_MANAGED_ENTITIES_SUGGESTIONS,
  SEARCH_STACK_MANAGED_ENTITIES_SUGGESTIONS,
} from "./gql";

type UseIacManagementSuggestionsProps = {
  parentStackId?: string;
};

const useIacManagementSuggestions = ({ parentStackId }: UseIacManagementSuggestionsProps) => {
  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTERS_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const {
    data: accountData,
    previousData: accountPreviousData,
    refetch: accountRefetch,
    loading: accountLoading,
  } = useQuery(SEARCH_MANAGED_ENTITIES_SUGGESTIONS, {
    variables: {
      input: {
        fields: null,
        fullTextSearch: filterVariables.searchInput,
        predicates: filterVariables.predicates,
      },
    },
    skip: !filterVariablesLoaded || !!parentStackId,
  });

  const stackPredicates = parentStackId && [
    ...filterVariables.predicates,
    getStackPredicate(parentStackId),
  ];

  const {
    data: stackData,
    previousData: stackPreviousData,
    refetch: stackRefetch,
    loading: stackLoading,
  } = useQuery(SEARCH_STACK_MANAGED_ENTITIES_SUGGESTIONS, {
    variables: {
      stackId: parentStackId,
      input: {
        fields: null,
        fullTextSearch: filterVariables.searchInput,
        predicates: stackPredicates,
      },
    },
    skip: !filterVariablesLoaded || !parentStackId,
  });

  const cachedFiltersData = useMemo(() => {
    if (parentStackId) {
      return (
        stackData?.stack?.searchManagedEntitiesSuggestions?.fields ||
        stackPreviousData?.stack?.searchManagedEntitiesSuggestions?.fields ||
        []
      );
    } else {
      return (
        accountData?.searchManagedEntitiesSuggestions?.fields ||
        accountPreviousData?.searchManagedEntitiesSuggestions?.fields ||
        []
      );
    }
  }, [
    accountData?.searchManagedEntitiesSuggestions?.fields,
    stackData?.stack?.searchManagedEntitiesSuggestions?.fields,
    accountPreviousData?.searchManagedEntitiesSuggestions?.fields,
    stackPreviousData?.stack?.searchManagedEntitiesSuggestions?.fields,
    parentStackId,
  ]);

  const allResourcesQty = useMemo(() => {
    const filteredCount = parentStackId
      ? stackData?.stack?.searchManagedEntitiesSuggestions?.filteredCount
      : accountData?.searchManagedEntitiesSuggestions?.filteredCount;

    return filteredCount || 0;
  }, [stackData, accountData, parentStackId]);

  return {
    cachedFiltersData,
    refetch: parentStackId ? stackRefetch : accountRefetch,
    loading: parentStackId ? stackLoading : accountLoading,
    allResourcesQty,
  };
};

export default useIacManagementSuggestions;
